import { type DiffStrategy, type DiffTreeChange, diffTrees } from '@gitbook/util-tree-merge';

import { compareFiles } from '../files';
import type { Revision, RevisionFile, RevisionFiles } from '../types';

/** File from a revision, as a tree data structure */
export interface RevisionTreeFile {
    file: RevisionFile;
    diff?: 'added' | 'deleted' | 'modified';
}

/** Result when diffing revisions files */
export interface FilesDiffResult {
    files: RevisionTreeFile[];
    diffs: Map<string, DiffTreeChange<RevisionTreeFile>>;
}

/**
 * Diff two revisions and return a set of files
 */
export function diffRevisionFiles(
    current: Revision,
    parent: Revision,
    options: {
        /**
         * If false, deleted files will not be included in the result.
         */
        includeDeleted?: boolean;
    } = {}
): FilesDiffResult {
    const { includeDeleted = true } = options;

    const diffStrategy: DiffStrategy<RevisionTreeFile> = {
        getId: (node) => node.file.id,
        getChildren: () => [],
        setChildren: (node) => ({
            ...node,
        }),
        compareItems: (original, modified) => (compareFiles(original.file, modified.file) ? 1 : 0),
        annotateChange: (node, diff) => {
            if (!includeDeleted && diff.type === 'deleted') {
                return [];
            }

            return [
                {
                    ...node,
                    diff: diff.type,
                },
            ];
        },
    };

    const result = diffTrees(
        {
            before: createTreeFiles(parent.files),
            after: createTreeFiles(current.files),
        },
        diffStrategy
    );

    return {
        files: result.items,
        diffs: result.changes,
    };
}

/**
 * Create a tree from a set of files.
 */
export function createTreeFiles(files: RevisionFiles): RevisionTreeFile[] {
    return Object.entries(files)
        .map(([, file]) => {
            return { file };
        })
        .sort((a, b) => {
            return a.file.id.localeCompare(b.file.id);
        });
}
